/**
 * 图纸分片上传器（共享组件）
 * 素材库/图纸库通用：分片上传图纸到 MinIO 并建档，内置进度条
 * 上传逻辑统一走 api.uploadDrawingFile
 */
import { useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import type { DrawingFileItem } from '../types/api';
import styles from './DrawingUploader.module.css';

export const DRAWING_EXTS = ['pdf', 'dwg', 'dxf', 'step', 'stp', 'igs', 'jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff', 'zip', 'rar', 'doc', 'docx', 'xls', 'xlsx'];

interface Props {
  scope: 'material' | 'product';
  materialId?: number;
  productId?: number;
  onUploaded: (d: DrawingFileItem) => void;
  onError?: (msg: string) => void;
}

export default function DrawingUploader({ scope, materialId, productId, onUploaded, onError }: Props) {
  const { showToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = async (file: File) => {
    // 友好拦截：空文件 / 超大文件（后端限制 ≤500MB 且 file_size ≥1）
    if (file.size === 0) { showToast('error', `「${file.name}」是空文件（0 字节），无法上传`); onError?.('空文件'); return; }
    if (file.size > 500 * 1024 * 1024) { showToast('error', `「${file.name}」超过 500MB 上限`); onError?.('超大文件'); return; }
    setUploading(true);
    setProgress(0);
    try {
      const item = await api.uploadDrawingFile(
        file,
        { scope, material_id: materialId, product_id: productId },
        pct => setProgress(pct),
      );
      showToast('success', `图纸「${file.name}」上传成功`);
      onUploaded(item);
    } catch (err) {
      const msg = getErrorMessage(err);
      showToast('error', msg);
      onError?.(msg);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className={styles.uploader}>
      <label className={styles.uploaderBtn}>
        {uploading ? <Loader2 size={14} className={styles.spin} /> : <Upload size={14} />}
        {uploading ? `上传中 ${progress}%` : '选择图纸文件'}
        <input
          type="file"
          accept={DRAWING_EXTS.map(e => '.' + e).join(',')}
          style={{ display: 'none' }}
          disabled={uploading}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </label>
      {uploading && (
        <div className={styles.progressTrack}><div className={styles.progressFill} style={{ width: `${progress}%` }} /></div>
      )}
    </div>
  );
}
